import * as THREE from 'three';
import rollbar from 'rollbar-browser';
import immutable from 'immutable';
import 'jspolyfill-array.prototype.find';
import 'es6-promise/auto';
import snabbdom from 'snabbdom';
import h from 'snabbdom/h';
import snabbdomClass from 'snabbdom/modules/class'; // makes it easy to toggle classes
import snabbdomProps from 'snabbdom/modules/props'; // for setting properties on DOM elements
import snabbdomAttrs from 'snabbdom/modules/attributes';
import snabbdomStyle from 'snabbdom/modules/style'; // handles styling on elements with support for animations
import snabbdomEvents from 'snabbdom/modules/eventlisteners'; // attaches event listeners
import eventEmitter from './utils/eventEmitter';
import ramda from 'ramda';
const { mapObjIndexed }  = ramda;

import callApi from './store/api';
// import createLogger from 'redux-logger';
import clientMiddleware from './store/clientMiddleware';

import animation from './modules/animation';
import annotations from './modules/annotations';
import sceneGraph from './modules/sceneGraph';
import sceneIO from './modules/sceneIO';
import assets from './modules/assets';
import player from './modules/player';
import scene from './modules/scene';
import plugins, { setApi } from './modules/plugins';
import configuration from './modules/configuration';
import selection from './modules/selection';
import commands, { addCommand } from './modules/commands';

import operatorRecords from './operators';
import primitives from './operators/primitives';

import buildStore from './store';
import bindActionCreatorsAndSelectors from './store/bindActionCreatorsAndSelectors';

import playerView from './components/player';

import tools from './translators/tools';
import threeTranslator from './translators/three';

import { startAutoRotate, autoRotateTool } from './autoRotate';

if (__BROWSER__) {
  window.WebVRConfig.CARDBOARD_UI_DISABLED = true;
  window.WebVRConfig.ROTATE_INSTRUCTIONS_DISABLED = true;
  window.WebVRConfig.BUFFER_SCALE = 1.0;
}

const reducers = {
  animation: animation.reducer,
  annotations: annotations.reducer,
  assets: assets.reducer,
  commands: commands.reducer,
  sceneGraph: sceneGraph.reducer(operatorRecords),
  sceneIO: sceneIO.reducer,
  player: player.reducer,
  plugins: plugins.reducer,
  selection: selection.reducer,
};

const patch = snabbdom.init([snabbdomClass, snabbdomProps, snabbdomAttrs, snabbdomStyle, snabbdomEvents]);

// const middleware = [logger()];
// const loggerMiddleware = createLogger({
//   timestamp: true, duration: true, transformer: (state) => state.toJS()
// });
let middleware = [clientMiddleware];
// if (false) middleware.push(loggerMiddleware);

const deps = {
  immutable,
  patch,
  h,
  ramda,
  THREE,
};


/**
 * The main function. This function initializes the player for a given DOM Node.
 *
 * @param {(String|DOMNode)} elOrId - the DOM Node or String id of a DOM Node.
 * @param {Object} options
 * @param {Function} options.callback - an optional initialization function that will be called (with api as argument) before the first render.
 * @returns {API} The clara playera API.
 * @public
 */
function claraplayer(elOrId, opts) {
  let initializationFn;
  let options;
  if (typeof opts === 'function') {
    initializationFn = opts;
    options = {};
    console.warn('DEPRECATED: second argument to `claraplayer` should be an options object');
  } else {
    options = opts || {};
    initializationFn = options.callback;
  }

  const Rollbar = __ROLLBAR_TOKEN__ && rollbar.init({
    accessToken: __ROLLBAR_TOKEN__,
    verbose: false,
    captureUncaught: !options.devMode,
    payload: {
      environment: __ENV__,
      context: 'playerv2',
    },
  });

  const parentEl = typeof elOrId === 'string' ? document.getElementById(elOrId) : elOrId;
  parentEl.appendChild(document.createElement('div'));
  const rect = parentEl.getBoundingClientRect();
  if (!rect.height) {
    throw new Error('Player element must have a height');
  }

  const store = buildStore(reducers, operatorRecords, primitives, middleware);
  if (Rollbar) store.useRollbar(Rollbar);
  let dispatch = store.dispatch.bind(store);
  const translator = threeTranslator(store);
  store.setTranslator(translator);
  store.setApi(callApi);

  const actions = bindActionCreatorsAndSelectors(store, {
    animation, annotations, assets, commands, configuration, scene, sceneIO, sceneGraph, player, plugins, selection,
  });

  let api = Object.assign({}, eventEmitter(), {
    deps,
    three: {
      scene: translator.scene,
      camera: translator.camera,
    },
    _store: store,
  }, actions);
  api.version = __VERSION__;

  if (initializationFn) initializationFn(api);

  const el = parentEl.children[0];
  const parentStyle = window.getComputedStyle(parentEl, null);
  const isFlex = parentStyle.display === 'flex';
  const isRelative = parentStyle.position === 'relative';
  let vnode = patch(el, playerView(store, { isFlex, isRelative, parentEl }));

  store.subscribe(function (events) { //try {
      vnode = patch(vnode, playerView(store, { isFlex, isRelative, parentEl }));
      if (events.length) events.forEach((e) => { api.emit(e.type, ...e.args); });
      api.emit('change');
      //} catch (e) {
      //  store.error(e);
      //}
    });

  dispatch(setApi(api));

  mapObjIndexed((tool, toolName) => {
    dispatch(addCommand(typeof tool === 'function' ? tool(store, translator) : tool, toolName));
  }, tools);

  const autoRotateToolObj = autoRotateTool(store);
  dispatch(addCommand(autoRotateToolObj, 'autoRotate'));

  api.on('loaded', () => {
    // TODO: zoom camera
    autoRotateToolObj.tool.start(1000);
  });

  return api;
};

claraplayer.deps = deps;

claraplayer.hasWebGL = function () {
  const canvas = document.createElement('canvas');
  return !!canvas.getContext('webgl') || !!canvas.getContext('experimental-webgl');
};

module.exports = claraplayer;



// WEBPACK FOOTER //
// ./src/player.js